import { useState, useRef, useEffect } from 'react'
import { useTheme } from 'styled-components'
import useListData from '../../hooks/useListData'
import useApi from '../../hooks/useApi'
import useTranslation from '../../hooks/useTranslation'
import WarehouseHallsView from './WarehouseHallsView'
import useCanvasGestures from './useCanvasGestures'
import {
    Wrap, Toolbar, ToolBtn, Btn,
    ZoomControl, ZoomBtn, ZoomLabel, SelectionPanel, PanelLabel, Hint,
} from './designerShell'
import { Icon_Fit, Icon_Sync } from '../../styles/icons'

// ---------------------------------------------------------------------------
// Geometry / layout
// ---------------------------------------------------------------------------
const SNAP = 100
const ENTRY_COLOR = '#22c55e'
const EXIT_COLOR = '#ef4444'
const DEFAULT_W = 20000
const DEFAULT_H = 14000
const SITE_GAP = 8000
const ZOOM_DISPLAY_REF = 0.02   // a full-site fit reads ~20%

function snapValue(v, tol = SNAP) { return Math.round(v / tol) * tol }
function screenToMm(sx, sy, tx, ty, scale) { return { x: (sx - tx) / scale, y: (sy - ty) / scale } }

// Blocks: use saved siteGeometry, else auto-place unplaced ones left-to-right.
// Entry/exit default to the left/right edge midpoints when a gate is unset.
function computeSiteLayout(warehouses) {
    let cursorX = 0
    return warehouses.map(w => {
        const placed = !!(w.siteGeometry?.width && w.siteGeometry?.height)
        const geometry = placed
            ? { ...w.siteGeometry, rotation: 0 }
            : { x: cursorX + DEFAULT_W / 2, y: DEFAULT_H / 2, width: DEFAULT_W, height: DEFAULT_H, rotation: 0 }
        if (!placed) cursorX += DEFAULT_W + SITE_GAP
        const entry = (w.entryPoint?.x != null) ? w.entryPoint : { x: geometry.x - geometry.width / 2, y: geometry.y }
        const exit = (w.exitPoint?.x != null) ? w.exitPoint : { x: geometry.x + geometry.width / 2, y: geometry.y }
        return { ...w, _placed: placed, geometry, _entry: entry, _exit: exit }
    })
}

function computeBBox(blocks) {
    const pts = []
    blocks.forEach(b => {
        const g = b.geometry
        pts.push({ x: g.x - g.width / 2, y: g.y - g.height / 2 }, { x: g.x + g.width / 2, y: g.y + g.height / 2 })
        pts.push(b._entry, b._exit)
    })
    if (!pts.length) return null
    const xs = pts.map(p => p.x), ys = pts.map(p => p.y)
    return { minX: Math.min(...xs), minY: Math.min(...ys), maxX: Math.max(...xs), maxY: Math.max(...ys) }
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
// Loads one warehouse's halls and fits them (read-only) into its site block.
function SiteBlockHalls({ warehouseId, block }) {
    const { data } = useListData({ endpoint: `warehouse/${warehouseId}/hall`, order: ['name', 'asc'] })
    const halls = (data?.pages?.flatMap(p => p.data) || []).filter(h => h.geometry?.width && h.geometry?.height)
    if (!halls.length) return null
    const xs = [], ys = []
    halls.forEach(h => {
        const g = h.geometry
        xs.push(g.x - g.width / 2, g.x + g.width / 2)
        ys.push(g.y - g.height / 2, g.y + g.height / 2)
    })
    const minX = Math.min(...xs), maxX = Math.max(...xs), minY = Math.min(...ys), maxY = Math.max(...ys)
    const bboxW = (maxX - minX) || 1, bboxH = (maxY - minY) || 1
    const g = block.geometry
    const pad = Math.min(g.width, g.height) * 0.1
    const scale = Math.min((g.width - 2 * pad) / bboxW, (g.height - 2 * pad) / bboxH)
    const bcx = (minX + maxX) / 2, bcy = (minY + maxY) / 2
    const tx = g.x - scale * bcx, ty = g.y - scale * bcy
    return (
        <g transform={`translate(${tx} ${ty}) scale(${scale})`} pointerEvents="none">
            <WarehouseHallsView halls={halls} />
        </g>
    )
}

function BlockShape({ block, selected, onBodyDown, onGateDown }) {
    const theme = useTheme()
    const g = block.geometry
    if (!g || !g.width || !g.height) return null
    const border = Math.max(60, Math.min(g.width, g.height) * 0.012)
    const fontSize = Math.max(300, g.width * 0.05)
    const gateR = Math.max(300, Math.min(g.width, g.height) * 0.05)
    const gateFont = gateR * 1.1
    return (
        <g>
            {/* block body */}
            <g style={{ cursor: 'move' }} onPointerDown={e => onBodyDown(e, block)}>
                <rect
                    x={g.x - g.width / 2}
                    y={g.y - g.height / 2}
                    width={g.width}
                    height={g.height}
                    rx={border * 2}
                    fill={theme.card.background}
                    fillOpacity={0.4}
                    stroke={selected ? theme.btn.primary : theme.area.border}
                    strokeWidth={selected ? border * 1.8 : border}
                    strokeDasharray={block._placed ? undefined : `${border * 5} ${border * 3}`}
                />
                <SiteBlockHalls warehouseId={block.id} block={block} />
                <text
                    x={g.x}
                    y={g.y - g.height / 2 - border * 2}
                    textAnchor="middle"
                    fontSize={fontSize}
                    fontWeight="700"
                    fill={theme.font.label}
                    stroke={theme.page.background}
                    strokeWidth={fontSize * 0.16}
                    paintOrder="stroke"
                    pointerEvents="none"
                >
                    {block.name || block.code}
                </text>
            </g>
            {/* entry / exit gates */}
            {[{ p: block._entry, color: ENTRY_COLOR, label: 'IN', which: 'entry' },
              { p: block._exit, color: EXIT_COLOR, label: 'OUT', which: 'exit' }].map(gate => (
                <g key={gate.which} style={{ cursor: 'grab' }} onPointerDown={e => onGateDown(e, block, gate.which)}>
                    <circle cx={gate.p.x} cy={gate.p.y} r={gateR} fill={gate.color} fillOpacity={0.9} stroke={theme.card.background} strokeWidth={gateR * 0.18} />
                    <text x={gate.p.x} y={gate.p.y} textAnchor="middle" dominantBaseline="middle" fontSize={gateFont} fontWeight="700" fill="#fff" pointerEvents="none">{gate.label}</text>
                </g>
            ))}
        </g>
    )
}

function ResizeHandles({ g, scale, onDown }) {
    const theme = useTheme()
    if (!g) return null
    const hw = g.width / 2, hh = g.height / 2
    const u = 1 / scale
    const corners = [
        { cx: g.x - hw, cy: g.y - hh, dir: 'nw', cur: 'nwse-resize' },
        { cx: g.x + hw, cy: g.y - hh, dir: 'ne', cur: 'nesw-resize' },
        { cx: g.x + hw, cy: g.y + hh, dir: 'se', cur: 'nwse-resize' },
        { cx: g.x - hw, cy: g.y + hh, dir: 'sw', cur: 'nesw-resize' },
    ]
    const hs = 13 * u, hit = 26 * u
    return (
        <g>
            {corners.map(h => (
                <g key={h.dir} style={{ cursor: h.cur }} onPointerDown={e => { e.stopPropagation(); onDown(e, h.dir) }}>
                    <rect x={h.cx - hit / 2} y={h.cy - hit / 2} width={hit} height={hit} fill="transparent" />
                    <rect x={h.cx - hs / 2} y={h.cy - hs / 2} width={hs} height={hs} fill={theme.card.background} stroke={theme.btn.primary} strokeWidth={2 * u} />
                </g>
            ))}
        </g>
    )
}

// ---------------------------------------------------------------------------
// Main
// ---------------------------------------------------------------------------
function WarehouseSiteDesigner() {
    const theme = useTheme()
    const { t } = useTranslation()
    const { getMe, putMe, postMe } = useApi()
    const svgRef = useRef(null)
    const wrapRef = useRef(null)
    const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 })

    useEffect(() => {
        function measure() {
            const el = wrapRef.current
            if (!el) return
            const rect = el.getBoundingClientRect()
            const height = Math.max(200, Math.floor(window.innerHeight - rect.top))
            setCanvasSize({ width: Math.round(rect.width), height })
        }
        measure()
        window.addEventListener('resize', measure)
        const obs = new ResizeObserver(measure)
        if (wrapRef.current) obs.observe(wrapRef.current)
        return () => { window.removeEventListener('resize', measure); obs.disconnect() }
    }, [])

    const [vp, setVp] = useState({ scale: 0.01, tx: 80, ty: 80 })
    const [mode, setMode] = useState('select')
    const [selected, setSelected] = useState(null)
    const [rebuilding, setRebuilding] = useState(false)
    const dragRef = useRef(null)
    const panRef = useRef(null)

    const { data: warehousePages, refetch: refetchWarehouses } = useListData({ endpoint: 'warehouse', order: ['name', 'asc'] })
    const warehouses = warehousePages?.pages?.flatMap(p => p.data) || []
    const blocks = computeSiteLayout(warehouses)

    function getSvgRect() {
        return svgRef.current?.getBoundingClientRect() || { left: 0, top: 0, width: 800, height: 600 }
    }
    function toMm(clientX, clientY) {
        const rect = getSvgRect()
        return screenToMm(clientX - rect.left, clientY - rect.top, vp.tx, vp.ty, vp.scale)
    }

    function zoomToFit() {
        const bbox = computeBBox(blocks)
        if (!bbox) return
        const rect = getSvgRect()
        if (!rect.width || !rect.height) return
        const pad = 80
        const bboxW = bbox.maxX - bbox.minX || 1
        const bboxH = bbox.maxY - bbox.minY || 1
        const scale = Math.min((rect.width - pad * 2) / bboxW, (rect.height - pad * 2) / bboxH)
        const tx = (rect.width - bboxW * scale) / 2 - bbox.minX * scale
        const ty = (rect.height - bboxH * scale) / 2 - bbox.minY * scale
        setVp({ scale, tx, ty })
    }

    // Wheel, the +/- buttons and pinch-to-zoom, shared with the layout designer.
    const gestures = useCanvasGestures({
        svgRef, vp, setVp, minScale: 0.001, maxScale: 2,
        isBusy: () => !!dragRef.current,
        onPinchStart: () => { panRef.current = null },
    })
    const zoomBy = gestures.zoomBy

    // Only reached for empty-canvas presses — blocks, gates and handles all
    // stopPropagation on their own pointerdown.
    function startPan(e) {
        panRef.current = { startX: e.clientX, startY: e.clientY, tx: vp.tx, ty: vp.ty }
    }
    function startBlockDrag(e, block) {
        if (mode !== 'select') return
        e.stopPropagation()
        const g = block.geometry
        setSelected({ type: 'warehouse', id: block.id, data: { ...block } })
        const mm = toMm(e.clientX, e.clientY)
        dragRef.current = { dir: 'move', startMmX: mm.x, startMmY: mm.y, origX: g.x, origY: g.y, origW: g.width, origH: g.height }
    }
    function startResize(e, dir) {
        if (!selected) return
        const mm = toMm(e.clientX, e.clientY)
        const g = selected.data.geometry
        dragRef.current = { dir, startMmX: mm.x, startMmY: mm.y, origX: g.x, origY: g.y, origW: g.width, origH: g.height }
    }
    function startGateDrag(e, block, which) {
        if (mode !== 'select') return
        e.stopPropagation()
        const pt = which === 'entry' ? block._entry : block._exit
        setSelected({ type: which, id: block.id, data: { ...block }, point: { x: pt.x, y: pt.y } })
        const mm = toMm(e.clientX, e.clientY)
        dragRef.current = { dir: 'gate', startMmX: mm.x, startMmY: mm.y, origX: pt.x, origY: pt.y }
    }

    function onPointerMove(e) {
        if (gestures.pointerMove(e)) return

        const mm = toMm(e.clientX, e.clientY)
        const d = dragRef.current
        if (d) {
            const dx = mm.x - d.startMmX, dy = mm.y - d.startMmY
            if (d.dir === 'gate') {
                setSelected(prev => ({ ...prev, point: { x: snapValue(d.origX + dx), y: snapValue(d.origY + dy) } }))
            } else if (d.dir === 'move') {
                setSelected(prev => ({ ...prev, data: { ...prev.data, geometry: { ...prev.data.geometry, x: snapValue(d.origX + dx), y: snapValue(d.origY + dy) } } }))
            } else {
                // corner resize (axis-aligned; blocks are never rotated)
                const sdx = snapValue(dx), sdy = snapValue(dy)
                let newW = d.origW, newH = d.origH, cx = 0, cy = 0
                if (d.dir.includes('e')) { newW = Math.max(SNAP, d.origW + sdx); cx = (newW - d.origW) / 2 }
                if (d.dir.includes('w')) { newW = Math.max(SNAP, d.origW - sdx); cx = -(newW - d.origW) / 2 }
                if (d.dir.includes('s')) { newH = Math.max(SNAP, d.origH + sdy); cy = (newH - d.origH) / 2 }
                if (d.dir.includes('n')) { newH = Math.max(SNAP, d.origH - sdy); cy = -(newH - d.origH) / 2 }
                setSelected(prev => ({ ...prev, data: { ...prev.data, geometry: { ...prev.data.geometry, x: d.origX + cx, y: d.origY + cy, width: newW, height: newH } } }))
            }
            return
        }
        if (panRef.current) {
            const p = panRef.current
            setVp(prev => ({ ...prev, tx: p.tx + (e.clientX - p.startX), ty: p.ty + (e.clientY - p.startY) }))
        }
    }

    async function onPointerUp(e) {
        // A pinch moves the viewport, never the drawing — nothing to save.
        if (gestures.pointerUp(e)) return

        const d = dragRef.current
        dragRef.current = null
        panRef.current = null
        if (d && selected) await saveSelection(selected)
    }

    async function saveSelection(sel) {
        const w = warehouses.find(x => x.id === sel.id)
        if (!w) return
        try {
            if (sel.type === 'warehouse') {
                const g = sel.data.geometry
                const body = { ...w, siteGeometry: { x: g.x, y: g.y, width: g.width, height: g.height, rotation: 0 } }
                // Move any explicitly-set gates with the block (attached).
                const og = w.siteGeometry
                if (og && og.x != null) {
                    const dx = g.x - og.x, dy = g.y - og.y
                    if (w.entryPoint?.x != null) body.entryPoint = { x: w.entryPoint.x + dx, y: w.entryPoint.y + dy }
                    if (w.exitPoint?.x != null) body.exitPoint = { x: w.exitPoint.x + dx, y: w.exitPoint.y + dy }
                }
                await putMe('warehouse', body, sel.id)
            } else if (sel.type === 'entry') {
                await putMe('warehouse', { ...w, entryPoint: sel.point }, sel.id)
            } else if (sel.type === 'exit') {
                await putMe('warehouse', { ...w, exitPoint: sel.point }, sel.id)
            }
            refetchWarehouses()
        } catch (_) { /* ignore */ }
    }

    async function rebuildSite() {
        // The ruleset loads all warehouses; any real warehouse id anchors the op.
        const anchor = warehouses[0]?.id
        if (!anchor) return
        setRebuilding(true)
        try { await postMe(`warehouse/${anchor}/rebuildSiteRouting`, {}) } catch (_) { /* ignore */ }
        setRebuilding(false)
    }

    // Live geometry for the selected block/gate while dragging.
    const selById = selected ? selected.id : null
    const scaleLabel = `${Math.round(vp.scale / ZOOM_DISPLAY_REF * 100)}%`

    return (
        <Wrap ref={wrapRef} style={canvasSize.height ? { height: canvasSize.height } : undefined}>
            <Toolbar>
                <ToolBtn onClick={zoomToFit}
                    icon={Icon_Fit} label={t('warehouse.site.zoom.fit')} />
                <ZoomControl>
                    <ZoomBtn onClick={() => zoomBy(0.8)}>−</ZoomBtn>
                    <ZoomLabel>{scaleLabel}</ZoomLabel>
                    <ZoomBtn onClick={() => zoomBy(1.25)}>+</ZoomBtn>
                </ZoomControl>
                <ToolBtn onClick={rebuildSite} disabled={rebuilding}
                    icon={Icon_Sync} label={t('warehouse.site.rebuild')}
                    text={rebuilding ? '…' : undefined} />
            </Toolbar>

            <svg
                ref={svgRef}
                width={canvasSize.width || undefined}
                height={canvasSize.height || undefined}
                style={{
                    display: 'block',
                    background: theme.page.background,
                    cursor: 'grab',
                    // Without this the browser claims every touch for page
                    // scroll and pinch, and none of the handlers below ever run.
                    touchAction: 'none',
                }}
                onPointerDown={e => {
                    if (e.button !== 0) return
                    if (gestures.pointerDown(e)) return  // second finger: pinch, not pan
                    setSelected(null)
                    startPan(e)
                }}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
            >
                <g transform={`translate(${vp.tx} ${vp.ty}) scale(${vp.scale})`}>
                    {blocks.map(b => {
                        // Reflect live drag state onto the selected block/gate.
                        let block = b
                        if (selById === b.id && selected.type === 'warehouse') {
                            // Gates are attached to the warehouse: follow the block live.
                            const g = selected.data.geometry
                            const dx = g.x - b.geometry.x, dy = g.y - b.geometry.y
                            block = {
                                ...b,
                                geometry: g,
                                _entry: (b.entryPoint?.x != null) ? { x: b.entryPoint.x + dx, y: b.entryPoint.y + dy } : { x: g.x - g.width / 2, y: g.y },
                                _exit: (b.exitPoint?.x != null) ? { x: b.exitPoint.x + dx, y: b.exitPoint.y + dy } : { x: g.x + g.width / 2, y: g.y },
                            }
                        }
                        if (selById === b.id && selected.type === 'entry') block = { ...block, _entry: selected.point }
                        if (selById === b.id && selected.type === 'exit') block = { ...block, _exit: selected.point }
                        return (
                            <BlockShape
                                key={b.id}
                                block={block}
                                selected={selById === b.id}
                                onBodyDown={startBlockDrag}
                                onGateDown={startGateDrag}
                            />
                        )
                    })}

                    {selected && selected.type === 'warehouse' && selected.data.geometry && (
                        <ResizeHandles g={selected.data.geometry} scale={vp.scale} onDown={startResize} />
                    )}
                </g>
            </svg>

            {selected && (
                <SelectionPanel>
                    <PanelLabel>
                        <small>{selected.type === 'entry' ? t('warehouse.site.panel.entry')
                            : selected.type === 'exit' ? t('warehouse.site.panel.exit')
                            : t('warehouse.site.panel.warehouse')}</small>
                        <b>{selected.data.name || selected.data.code || '—'}</b>
                    </PanelLabel>
                    <Btn onClick={() => setSelected(null)}>{t('warehouse.site.panel.deselect')}</Btn>
                </SelectionPanel>
            )}

            <Hint>{t('warehouse.site.hint')}</Hint>
        </Wrap>
    )
}

export default WarehouseSiteDesigner
